// Modern Layout with Brand Colors & Enhanced Styling
import React, { useState } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  useMediaQuery,
  alpha,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close,
  Dashboard,
  People,
  Security,
  PointOfSale,
  AccountCircle,
  Logout,
  Settings,
  Notifications,
  ShoppingCart,
  Assignment,
  Assessment,
  CorporateFare,
  Waves,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";

const drawerWidth = 280;

const menuItems = [
  {
    text: "Dashboard",
    icon: <Dashboard />,
    path: "/dashboard",
    roles: ["*"],
    module_code: "dashboard",
  },
  {
    text: "Vendor Master",
    icon: <CorporateFare />,
    path: "/vendors",
    roles: ["SALES", "SUPERADMIN"],
    module_code: "vendor",
  },
  {
    text: "Sales Tracker",
    icon: <PointOfSale />,
    path: "/sales",
    roles: ["SALES", "ACCOUNTS", "SUPERADMIN"],
    module_code: "sales_tracker",
  },
  {
    text: "Purchase Tracker",
    icon: <ShoppingCart />,
    path: "/purchase",
    roles: ["SALES", "ACCOUNTS", "SUPERADMIN"],
    module_code: "purchase_tracker",
  },
  {
    text: "Order Tracker",
    icon: <Assignment />,
    path: "/orders",
    roles: ["SALES", "ACCOUNTS", "SUPERADMIN"],
    module_code: "order_tracker",
  },
  {
    text: "Reports",
    icon: <Assessment />,
    path: "/reports",
    roles: ["SUPERADMIN"],
    module_code: "reports",
  },
  {
    text: "User Management",
    icon: <People />,
    path: "/users",
    roles: ["SUPERADMIN"],
    module_code: "user_management",
  },
  {
    text: "Role Management",
    icon: <Security />,
    path: "/roles",
    roles: ["SUPERADMIN"],
    module_code: "role_management",
  },
  {
    text: "Permissions",
    icon: <Settings />,
    path: "/permissions",
    roles: ["SUPERADMIN"],
  },
  {
    text: "Currency Master",
    icon: <CorporateFare />,
    path: "/currency",
    roles: ["SUPERADMIN"],
    module_code: "currency",
  },
];

const pageTitles = {
  "/profile": "My Profile",
};

const Layout = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, canReadModule } = useAuth();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);
  const openProfileMenu = (e) => setAnchorEl(e.currentTarget);
  const closeProfileMenu = () => setAnchorEl(null);

  const logoutUser = () => {
    logout();
    navigate("/login");
  };

  const canAccess = (item) => {
    if (item.roles.includes("*")) return true;
    if (!user) return false;
    if (user.is_superuser) return true;
    if (item.module_code) return canReadModule(item.module_code);
    return item.roles.includes(user.role_name);
  };

  // ---------------------------------------------------------------------------
  // FIX: the desktop drawer toggles via drawerOpen;
  // AppBar + main content widths respond to the state.
  // Mobile drawer uses its own overlay (temporary variant) — unchanged.
  // ---------------------------------------------------------------------------

  const drawerContent = (
    <Box
      sx={{
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #005b82 0%, #003d5c 100%)",
        color: "white",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            mb: 1,
          }}
        >
          <IconButton
            onClick={toggleDrawer}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              "&:hover": { background: "rgba(255,255,255,0.1)" },
            }}
          >
            {/* <Close /> */}
          </IconButton>
          <Waves
            sx={{
              fontSize: 32,
              color: "#e0f7ff",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
            }}
          />
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              background: "linear-gradient(135deg, #e0f7ff 0%, #ffffff 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            OPS Tracker
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ opacity: 0.8, color: "#e0f7ff", fontWeight: 500 }}
        >
          Maritime Operations Platform
        </Typography>
      </Box>

      {/* Navigation */}
      <List sx={{ flexGrow: 1, py: 2, px: 1 }}>
        {menuItems.filter(canAccess).map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) toggleDrawer();
              }}
              sx={{
                mx: 1,
                borderRadius: 2,
                py: 1.25,
                transition: "all 0.3s ease",
                border: "1px solid transparent",
                "&.Mui-selected": {
                  background:
                    "linear-gradient(135deg, #2586bc 0%, #005b82 100%)",
                  color: "white",
                  boxShadow: "0 8px 25px rgba(37, 134, 188, 0.3)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  "& .MuiListItemIcon-root": {
                    color: "white",
                    transform: "scale(1.1)",
                  },
                },
                "&:not(.Mui-selected)": {
                  color: "rgba(255,255,255,0.8)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.1)",
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.1)",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: "inherit",
                  transition: "all 0.3s ease",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: "0.95rem",
                  fontWeight: location.pathname === item.path ? 600 : 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: "rgba(255,255,255,0.05)",
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{ opacity: 0.7, color: "#e0f7ff", display: "block" }}
          >
            Signed in as
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: "white" }}>
            {user?.first_name} {user?.last_name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.6, color: "#e0f7ff", display: "block", mt: 0.5 }}
          >
            {user?.role_name}
          </Typography>
        </Box>

        <Typography
          sx={{
            mt: 2,
            fontSize: 11,
            opacity: 0.5,
            textAlign: "center",
            color: "#e0f7ff",
          }}
        >
          OPS v2.0 — Powered by MTPL
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", width: "100%", minHeight: "100vh" }}>
      {/* ── APP BAR ──────────────────────────────────────────────────── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)",
          color: "text.primary",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          // FIX: full width when drawer is closed, offset when open
          width: {
            xs: "100%",
            sm: drawerOpen ? `calc(100% - ${drawerWidth}px)` : "100%",
          },
          ml: { sm: 0 },
          transition: "width 0.3s ease",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            onClick={toggleDrawer}
            sx={{ display: "flex", mr: 2, color: "#005b82" }}
          >
            {/* FIX: show Close icon when drawer is open, Menu when closed */}
            {drawerOpen ? <Close /> : <MenuIcon />}
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              background: "linear-gradient(135deg, #005b82 0%, #2586bc 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {menuItems.find((i) => i.path === location.pathname)?.text ||
              pageTitles[location.pathname] ||
              "Dashboard"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={openProfileMenu}
              sx={{
                "&:hover": { transform: "scale(1.05)" },
                transition: "transform 0.2s ease",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#e83d33",
                  width: 36,
                  height: 36,
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(232, 61, 51, 0.3)",
                }}
              >
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeProfileMenu}
            PaperProps={{
              elevation: 4,
              sx: {
                borderRadius: 2,
                mt: 1.5,
                minWidth: 160,
                "& .MuiMenuItem-root": { px: 2, py: 1, fontSize: "0.9rem" },
              },
            }}
          >
            <MenuItem onClick={() => navigate("/profile")}>
              <AccountCircle sx={{ mr: 1.5, color: "#005b82" }} />
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={logoutUser}>
              <Logout sx={{ mr: 1.5, color: "#e83d33" }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── DRAWERS ──────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerOpen ? drawerWidth : 0 }, flexShrink: 0 }}
      >
        {/* Mobile — temporary overlay drawer */}
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={toggleDrawer}
          ModalProps={{
            keepMounted: true,
            disableScrollLock: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxShadow: "4px 0 20px rgba(0,0,0,0.1)",
              background: "#003d5c",
              overflowY: "auto",
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop — permanent drawer (now respects drawerOpen state) */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              // FIX: slide off-screen when closed instead of always visible
              transform: drawerOpen
                ? "translateX(0)"
                : `translateX(-${drawerWidth}px)`,
              transition: "transform 0.3s ease",
              width: drawerWidth,
              border: "none",
              boxShadow: "4px 0 20px rgba(0,0,0,0.08)",
              position: "fixed",
              top: 0,
              height: "100vh",
              overflowY: "auto",
              background: "#003d5c",
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────── */}
      <Box
        component={motion.main}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: 8,
          // FIX: width responds to drawer state on desktop
          width: "100%",
          overflow: "visible",
          transition: "width 0.3s ease",
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          minHeight: "100vh",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
